import {
  GTM_MAIN_EVENT_NAME,
  GTM_PAGEVIEW_DELAY_TRACK,
  GtmEvent,
  GtmEventTypes,
} from '.'
import {
  GQLBonusSet,
  GQLBonusSetVariantData,
  GQLProduct,
  GQLProductBundle,
  GQLProductVariant,
  Maybe,
} from '~/graphql/__generated__/interfaces'
import Detail from '~/pages/detail/index.vue'
import { SelectedBundles } from '~/components/common.types'
import DetailBonusSetPage from '~/pages/bonusSet/index.vue'
import { PageLoad } from '~/plugins/pageLoad'

export interface BaseProductData {
  articles: [BaseProductDataArticles]
  routeName?: string
}

export interface BaseProductDataArticles {
  id: string
  list?: string
  position?: number
  quantity?: number
  itemType?: string
  context?: string
  pageTypeList?: string
  routeName?: string
  bonusSetId?: string
  selectedBundles?: SelectedBundles
}

export interface ProductImpressionData extends BaseProductData {}

export interface ProductClickData extends BaseProductData {}

export interface ProductDetailData extends BaseProductData {}

export interface ProductClickEvent extends GtmEvent {
  detail: {
    eventType: GtmEventTypes.ProductClick
    data: ProductClickData
  }
}

export interface ProductImpressionEvent extends GtmEvent {
  detail: {
    eventType: GtmEventTypes.ProductImpression
    data: ProductImpressionData
  }
}

export interface ProductDetailEvent extends GtmEvent {
  detail: {
    eventType: GtmEventTypes.ProductDetail
    data: ProductDetailData
  }
}

export const isProductImpressionEvent = (
  evt: GtmEvent
): evt is ProductImpressionEvent =>
  evt.detail.eventType === GtmEventTypes.ProductImpression

export const isProductClickEvent = (evt: GtmEvent): evt is ProductClickEvent =>
  evt.detail.eventType === GtmEventTypes.ProductClick

export const createProductImpressEvent = (
  data: ProductImpressionData
): ProductImpressionEvent =>
  new CustomEvent(GTM_MAIN_EVENT_NAME, {
    bubbles: true,
    detail: { data, eventType: GtmEventTypes.ProductImpression },
  })

export const createProductClickEvent = (
  data: ProductClickData
): ProductClickEvent =>
  new CustomEvent(GTM_MAIN_EVENT_NAME, {
    bubbles: true,
    detail: { data, eventType: GtmEventTypes.ProductClick },
  })

export const createProductDetailEvent = (
  data: ProductDetailData
): ProductDetailEvent =>
  new CustomEvent(GTM_MAIN_EVENT_NAME, {
    bubbles: true,
    detail: { data, eventType: GtmEventTypes.ProductDetail },
  })

export interface GtmProduct {
  id: string
  name: string
  brand?: string
  price: number
  variant?: string
  category?: string
  list?: string
  position?: number
  quantity?: number
  /** priceHundreds */
  dimension32?: number
  /** rankingAverage */
  dimension33?: number
  /** rankingTotal */
  dimension34?: number
  /** priceBeforeDiscount */
  dimension36?: number
  /** discount */
  dimension37?: number
  /** labels */
  dimension38?: string
  /** availability */
  dimension39?: string
  /** productDivisionCode */
  dimension47?: string
  /** productDivisionName */
  dimension48?: string
  /** productTypeId */
  dimension49?: string
  /** productCategoryName */
  dimension50?: string
  /** priceWithoutVat */
  dimension51?: number
  /** vatValue */
  dimension52?: number
  /** extendedReturnValue */
  dimension53?: string
  /** extendedReturnPlacement */
  dimension54?: string
  /** productInsuranceValue */
  dimension76?: string
  /** productInsurancePlacement */
  dimension77?: string
  /** productWarrantyValue */
  dimension78?: string
  /** productWarrantyPlacement */
  dimension79?: string
  /** numberOfGifts */
  dimension80?: number
  /** valueOfGifts */
  dimension81?: number
  /** marketplacePartnerCode */
  dimension84?: string
  /** marketplacePartnerName */
  dimension85?: string
  /** crossSell */
  dimension87?: string
  /** bonusSetId */
  dimension128?: string
}

const crossSellOptions: Record<string, string> = {
  'detail.detail': 'product.detail',
  'detail.mallpayModal': 'product.detail.mallpayModal',
  'detail.recommended': 'product.detail-cross',
  'category.list': 'category',
  'campaign.list': 'campaign',
}

const SEARCH_SUGGESTER = 'search.whisperer'

export interface ProductVariantGtm extends GQLProduct {
  variant: GQLProductVariant
}

export const exportGtmProduct = (
  product: ProductVariantGtm,
  options: {
    quantity?: number
    position?: number
    list?: string
    context?: string
    bonusSetId?: string
    eventType?: GtmEventTypes
    selectedBundles?: SelectedBundles
  }
): GtmProduct => {
  const { variant, marketplace } = product
  const {
    position,
    quantity,
    list,
    context,
    eventType,
    selectedBundles,
    bonusSetId,
  } = options ?? {}

  const { price, vatRate, mainMenuPath } = variant
  const rankTotal = product.rank?.count ?? 0
  const rankAvg = product.rank?.rank ?? 0
  const [productCategoryName] = mainMenuPath?.slice(-1)
  const [productDivisionName] = mainMenuPath?.slice(0, 1)
  const minQuantity = quantity || 1

  const priceHundreds = Math.ceil((price * minQuantity) / 100) * 100

  const priceVatless = Math.ceil(
    (price * minQuantity) / ((100 + (vatRate ?? 0)) / 100)
  )

  const giftDetails = variant.giftDetails?.variants.reduce(
    (acc, gift) => {
      acc.count += gift.count
      acc.value += gift.variant?.price ?? 0
      return acc
    },
    {
      count: 0,
      value: 0,
    }
  )

  const dimension87Value =
    context && crossSellOptions[context]
      ? crossSellOptions[context]
      : context?.includes(SEARCH_SUGGESTER)
      ? SEARCH_SUGGESTER
      : null

  const marketplacePartnerMall = 'Mall'

  return {
    brand: variant.brand?.title ?? '',
    category: variant.mainMenuPath.join('/'),
    id: product.id,
    name: variant.title,
    price: variant.price,
    variant: variant.id,
    ...(list && { list }),
    // position can be 0
    ...(position !== undefined && { position }),
    ...(quantity && { quantity }),
    dimension32: priceHundreds,
    dimension33: rankAvg,
    dimension34: rankTotal,
    dimension36: variant.priceRrp ?? 0,
    dimension37: variant.discountPrice ?? 0,
    dimension38: variant.labels?.join(','),
    dimension39: availabilityKey(variant.availability?.key || ''),
    ...(product.division && { dimension47: product.division }),
    dimension48: productDivisionName,
    ...(product.productTypeId && { dimension49: product.productTypeId }),
    dimension50: productCategoryName,
    dimension51: priceVatless,
    dimension52: vatRate ?? 0,
    ...(eventType === GtmEventTypes.ProductDetail && {
      dimension76: getYearsFromBundles('insurance', variant.bundles),
      dimension77: context,
      dimension78: getYearsFromBundles('warranty', variant.bundles),
      dimension79: context,
    }),
    ...(eventType === GtmEventTypes.AddToCart && {
      dimension76: getYearsOfSelectedBundle(
        'insurance',
        variant.bundles,
        selectedBundles
      ),
      dimension77: context,
      dimension78: getYearsOfSelectedBundle(
        'warranty',
        variant.bundles,
        selectedBundles
      ),
      dimension79: context,
    }),
    ...(variant.giftDetails !== undefined && {
      dimension80: giftDetails?.count ?? 0,
      dimension81: giftDetails?.value ?? 0,
    }),
    dimension84: marketplace?.marketplacePartnerId ?? marketplacePartnerMall,
    dimension85: marketplace?.marketplacePartnerTitle ?? marketplacePartnerMall,
    ...(dimension87Value && {
      dimension87: dimension87Value,
    }),
    ...(bonusSetId && {
      dimension128: bonusSetId,
    }),
  }
}

const NA = 'NA'

const getBundlesByType = (
  type: string,
  bundles: Array<Maybe<GQLProductBundle>>
): Array<Maybe<GQLProductBundle>> => {
  if (!bundles || !bundles.length) {
    return []
  }

  return bundles.filter((item) => item?.type === type && item?.variants?.length)
}

const getYearsFromBundles = (
  type: string,
  bundles: Array<Maybe<GQLProductBundle>>
): string => {
  const filteredBundles = getBundlesByType(type, bundles)
  if (!filteredBundles.length) {
    return NA
  }

  const yearsArray: Array<number> = []
  for (const bundle of filteredBundles) {
    for (const variant of bundle?.variants || []) {
      if (variant && variant.__typename === 'ProductBundleVariant') {
        yearsArray.push(variant.yearsNumber)
      }
    }
  }

  const joined = yearsArray.sort((a, b) => a - b).join(',')
  return joined.length ? joined : NA
}

const getYearsOfSelectedBundle = (
  type: string,
  bundles: Array<Maybe<GQLProductBundle>>,
  selectedBundles?: SelectedBundles
): string => {
  const filteredBundles = getBundlesByType(type, bundles)
  if (!filteredBundles.length) {
    return NA
  }

  if (selectedBundles && selectedBundles[type] && selectedBundles[type][0]) {
    const selectedId = selectedBundles[type][0]
    for (const bundle of filteredBundles) {
      for (const variant of bundle?.variants || []) {
        if (
          variant &&
          variant.__typename === 'ProductBundleVariant' &&
          variant.id === selectedId
        ) {
          return String(variant.yearsNumber)
        }
      }
    }
  }

  return '0'
}

export const availabilityKey = (key: string) => {
  if (key === 'avail-digital-content') {
    return 'avail_digital_content'
  }
  return key.replace('-', '_')
}

export const exportGtmBonusSet = (
  bonusSet: GQLBonusSet,
  options: { quantity?: number; position?: number; list?: string }
): GtmProduct => {
  const { position, quantity, list } = options ?? {}

  const actualPrice = bonusSet?.price?.actualPrice

  if (typeof actualPrice !== 'number') {
    throw new TypeError(
      'exportGtmBonusSet actual price of bonuset should be number'
    )
  }

  const priceHundreds = Math.ceil(actualPrice / 100) * 100

  return {
    id: bonusSet.id,
    name: bonusSet.title,
    price: bonusSet.price.actualPrice ?? 0,
    // position can be 0
    ...(position !== undefined && { position }),
    ...(quantity && { quantity }),
    ...(list && { list }),
    dimension32: priceHundreds,
    dimension36: bonusSet.price.productsActualPrice ?? 0,
    dimension39: bonusSet.availabilityKey ?? '',
  }
}

const sendAfterPageLoad = (
  submitEvent: { (): void; (): void },
  $pageLoad: PageLoad
) => {
  if (parseInt($pageLoad.pageLoadTime()) > 0) {
    sendAfterPageLoadTimeout(submitEvent)
    return
  }

  if (document.readyState === 'complete') {
    sendAfterPageLoadTimeout(submitEvent)
  } else {
    window.addEventListener('load', function () {
      sendAfterPageLoadTimeout(submitEvent)
    })
  }
}

const sendAfterPageLoadTimeout = (submitEvent: { (): void; (): void }) => {
  setTimeout(() => {
    submitEvent()
  }, GTM_PAGEVIEW_DELAY_TRACK)
}

export const trackProductDetail = (detail: Detail, product: GQLProduct) => {
  const { $pageLoad } = detail

  sendAfterPageLoad(() => {
    if (detail.$pushAnalyticsEvent) {
      detail.$pushAnalyticsEvent(GtmEventTypes.ProductDetail, {
        articles: [
          {
            context: 'detail',
            id: product.mainVariant.id,
            itemType: 'product',
            quantity: 1,
          },
        ],
      })
    }
  }, $pageLoad)
}

export const trackProductBonusSetDetail = (
  detail: DetailBonusSetPage,
  product: GQLBonusSet
) => {
  const { $pageLoad } = detail

  const variantData = product?.variantData ?? []

  const articles = variantData.map((variant: GQLBonusSetVariantData) => ({
    context: 'detail',
    id: variant.id,
    itemType: 'product',
    bonusSetId: product.id,
    quantity: 1,
  })) as ProductDetailData['articles']

  sendAfterPageLoad(() => {
    if (detail.$pushAnalyticsEvent) {
      detail.$pushAnalyticsEvent(GtmEventTypes.ProductDetail, {
        articles,
      })
    }
  }, $pageLoad)
}
